import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { UserCheck, Search, Users } from 'lucide-react';
import { accountsApi, type AccountUser } from '../../../api/accounts';
import { WorkOSBadge, type WorkOSStatus } from '../../../components/shared/WorkOSBadge';
import UserDetailPanel from '../components/UserDetailPanel';

// ─── Hooks ──────────────────────────────────────────────────────────────────

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function relativeTime(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function userAvatar(user: AccountUser) {
  if (user.profile_picture_url) {
    return (
      <img
        src={user.profile_picture_url}
        alt={`${user.first_name} ${user.last_name}`}
        className="w-8 h-8 rounded-full object-cover"
      />
    );
  }
  const initials = `${user.first_name?.[0] ?? ''}${user.last_name?.[0] ?? ''}`.toUpperCase();
  return (
    <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-700 flex items-center justify-center text-xs font-semibold">
      {initials}
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export default function UsersPage() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState<AccountUser | null>(null);
  const debouncedSearch = useDebounce(search, 300);
  const navigate = useNavigate();
  const pageSize = 50;

  const { data, isLoading } = useQuery({
    queryKey: ['crm-account-users', debouncedSearch, page],
    queryFn: () =>
      accountsApi.listUsers({
        search: debouncedSearch || undefined,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      }),
    staleTime: 60_000,
  });

  const users = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);

  const statusCounts = useMemo(() => {
    const counts = { active: 0, invited: 0 };
    users.forEach((u) => {
      if (u.workos_status === 'active') counts.active++;
      else counts.invited++;
    });
    return counts;
  }, [users]);

  return (
    <div className="flex flex-col h-full bg-neutral-50">
      {/* Header */}
      <div className="px-6 pt-6 pb-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2.5 mb-1">
              <div className="w-8 h-8 rounded-lg bg-violet-50 border border-violet-100 flex items-center justify-center">
                <UserCheck size={16} className="text-violet-600" />
              </div>
              <h1 className="text-xl font-semibold text-neutral-900">Users</h1>
            </div>
            <p className="text-sm text-neutral-500 ml-[42px]">Product users across all customers</p>
          </div>
          <div className="flex items-center gap-2">
            {statusCounts.active > 0 && (
              <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20 font-medium">
                {statusCounts.active} active
              </span>
            )}
            {statusCounts.invited > 0 && (
              <span className="text-xs px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 ring-1 ring-amber-600/20 font-medium">
                {statusCounts.invited} invited
              </span>
            )}
            <span className="text-xs text-neutral-400 ml-1 tabular-nums">{total} total</span>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="px-6 pb-4">
        <div className="relative max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search users..."
            className="w-full pl-9 pr-3 py-2 bg-white border border-neutral-200 rounded-lg text-sm text-neutral-900 placeholder:text-neutral-400 outline-none focus:ring-1 focus:ring-coral-500 focus:border-transparent transition-shadow"
          />
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto mx-6 mb-6 bg-white rounded-xl border border-neutral-200 shadow-sm">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10">
            <tr className="border-b border-neutral-100 bg-neutral-50/95 backdrop-blur-sm">
              <th className="text-left px-5 py-3 text-xs font-medium text-neutral-500 uppercase tracking-wider">Name</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-neutral-500 uppercase tracking-wider">Email</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-neutral-500 uppercase tracking-wider">Customer</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-neutral-500 uppercase tracking-wider">Job Title</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-neutral-500 uppercase tracking-wider">Role</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-neutral-500 uppercase tracking-wider">Status</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-neutral-500 uppercase tracking-wider">Last Sign-In</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <tr key={i} className="border-b border-neutral-50">
                  {Array.from({ length: 7 }).map((_, j) => (
                    <td key={j} className="px-4 py-3.5">
                      <div className="h-4 w-24 bg-neutral-100 rounded animate-pulse" />
                    </td>
                  ))}
                </tr>
              ))
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-16 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-neutral-100 flex items-center justify-center">
                    <Users size={24} className="text-neutral-300" />
                  </div>
                  <p className="text-sm font-medium text-neutral-500 mb-1">
                    {search ? 'No users match your search' : 'No users found'}
                  </p>
                  <p className="text-xs text-neutral-400">
                    {search ? 'Try a different search term' : 'Run WorkOS sync to populate user data'}
                  </p>
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr
                  key={user.id}
                  onClick={() => setSelectedUser(user)}
                  className="border-b border-neutral-50 hover:bg-neutral-50/80 transition-colors cursor-pointer"
                >
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2.5">
                      {userAvatar(user)}
                      <span className="font-medium text-neutral-900">
                        {user.first_name} {user.last_name}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3.5 text-neutral-500">{user.email}</td>
                  <td className="px-4 py-3.5">
                    {user.company_id ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`../cu/${user.company_id}`);
                        }}
                        className="text-blue-600 hover:text-blue-700 hover:underline text-sm"
                      >
                        {user.company_name}
                      </button>
                    ) : (
                      <span className="text-neutral-300">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3.5 text-neutral-500">{user.job_title ?? '-'}</td>
                  <td className="px-4 py-3.5">
                    <span className="text-xs px-2 py-0.5 rounded-full capitalize bg-neutral-100 text-neutral-600">
                      {user.membership_role}
                    </span>
                  </td>
                  <td className="px-4 py-3.5">
                    <WorkOSBadge status={user.workos_status as WorkOSStatus} />
                  </td>
                  <td className="px-4 py-3.5 text-neutral-500">
                    {relativeTime(user.last_sign_in_at)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-neutral-100 bg-neutral-50/50">
            <span className="text-xs text-neutral-500 tabular-nums">{total} users</span>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 text-xs rounded-lg border border-neutral-200 text-neutral-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <span className="text-xs text-neutral-500 px-2 tabular-nums">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1.5 text-xs rounded-lg border border-neutral-200 text-neutral-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* User detail slide-out */}
      <UserDetailPanel user={selectedUser} onClose={() => setSelectedUser(null)} />
    </div>
  );
}
